import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';
import { useState } from 'react';
import z from 'zod';

export interface NewContactFields {
    firstName: string;
    middleName: string;
    lastName: string;
    contactEmail: string;
    phoneNumber: string;
}

interface Props {
    onClose: () => void;
    onSave: (contact: NewContactFields) => Promise<void>;
}

const CONTACT_SCHEMA = z.object({
    firstName: z.string().trim().min(1, 'First name is required'),
    lastName: z.string().trim().min(1, 'Last name is required'),
    contactEmail: z.email('Email must be valid'),
});

const EMPTY_CONTACT = {
    firstName: '',
    middleName: '',
    lastName: '',
    contactEmail: '',
    phoneNumber: '',
};

export default function AddContactModal({ onClose, onSave }: Props) {
    const [draftContact, setDraftContact] = useState(EMPTY_CONTACT);
    const [errors, setErrors] = useState<Record<string, string | undefined>>({});
    const [isSaving, setIsSaving] = useState(false);

    const getFieldErrors = (contactToValidate: typeof EMPTY_CONTACT) => {
        const validationResult = CONTACT_SCHEMA.safeParse(contactToValidate);

        if (validationResult.success) return {};

        const fieldErrors: Record<string, string> = {};
        for (const issue of validationResult.error.issues) {
            const field = issue.path[0];
            if (typeof field === 'string' && !fieldErrors[field]) {
                fieldErrors[field] = issue.message;
            }
        }

        return fieldErrors;
    };

    const handleFieldChange = (field: keyof typeof EMPTY_CONTACT, value: string) => {
        setDraftContact((prev) => {
            const nextContact = { ...prev, [field]: value };
            setErrors(getFieldErrors(nextContact));
            return nextContact;
        });
    };

    const handleSave = async () => {
        if (isSaving) return;

        const fieldErrors = getFieldErrors(draftContact);
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return;
        }

        setErrors({});
        setIsSaving(true);
        try {
            await onSave(draftContact);
        } finally {
            setIsSaving(false);
        }
    };

    const isFormValid = CONTACT_SCHEMA.safeParse(draftContact).success;

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add contact</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="First name"
                            required
                            fullWidth
                            value={draftContact.firstName}
                            onChange={(event) => handleFieldChange('firstName', event.target.value)}
                            error={Boolean(errors.firstName)}
                            helperText={errors.firstName}
                        />
                        <TextField
                            label="Middle name"
                            fullWidth
                            value={draftContact.middleName}
                            onChange={(event) =>
                                handleFieldChange('middleName', event.target.value)
                            }
                        />
                        <TextField
                            label="Last name"
                            required
                            fullWidth
                            value={draftContact.lastName}
                            onChange={(event) => handleFieldChange('lastName', event.target.value)}
                            error={Boolean(errors.lastName)}
                            helperText={errors.lastName}
                        />
                    </Stack>
                    <TextField
                        label="Email"
                        required
                        fullWidth
                        value={draftContact.contactEmail}
                        onChange={(event) => handleFieldChange('contactEmail', event.target.value)}
                        error={Boolean(errors.contactEmail)}
                        helperText={errors.contactEmail}
                    />
                    <TextField
                        label="Cell number"
                        fullWidth
                        value={draftContact.phoneNumber}
                        onChange={(event) => handleFieldChange('phoneNumber', event.target.value)}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack direction="row" spacing={1}>
                    <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || !isFormValid}
                    >
                        Add
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
